import React, {useState} from "react";
import {Alert, FormControlLabel, HStack, Radio, RadioGroup, Typography, VStack} from "../primitives";
import { MAX_VISIBLE_MULTIPLE_MATCHES } from "../../constants/bcrReview.constants";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import BcrReviewMatchSection from "./BcrReviewMatchSection";

const BcrReviewMultipleMatchSection = ({
  t,
  links,
  incomingFiKey,
  incomingFiLabel,
  pendingBcr,
  selectedLinkId,
  onSelectLink,
  notAvailableLabel,
  hideDuplicateWarning,
}) => {
  const visibleLinks = links.slice(0, MAX_VISIBLE_MULTIPLE_MATCHES);
  const hasMoreMatches = links.length > MAX_VISIBLE_MULTIPLE_MATCHES;

  return (
    <VStack spacing={2} className="bcr-review__section">
      <Typography variant="h2">
        {t("erps.billermanagement.bcrreview.multiple.heading", "Multiple matches")}
      </Typography>
      {hasMoreMatches && (
        <Alert severity="warning" className="bcr-review__duplicate-warning">
          <Typography variant="body1" component="p">
            {t(
              "erps.billermanagement.bcrreview.multiple.limitwarning",
              "Only the first {{limit}} matches are shown. {{count}} matches were found.",
              { limit: MAX_VISIBLE_MULTIPLE_MATCHES, count: links.length }
            )}
          </Typography>
        </Alert>
      )}
      <RadioGroup
        value={selectedLinkId}
        onChange={onSelectLink}
        name="bcr-review-match-selection"
        className="bcr-review__match-list"
      >
        {visibleLinks.map((link, index) => (
          <BcrReviewMultipleMatchCard
            key={link.id ?? index}
            t={t}
            link={link}
            matchIndex={index + 1}
            pendingBcr={pendingBcr}
            incomingFiKey={incomingFiKey}
            incomingFiLabel={incomingFiLabel}
            notAvailableLabel={notAvailableLabel}
            hideDuplicateWarning={hideDuplicateWarning}
          />
        ))}
      </RadioGroup>
    </VStack>
  );
};

const BcrReviewMultipleMatchCard = ({t, link, matchIndex, pendingBcr, incomingFiKey, incomingFiLabel, notAvailableLabel, hideDuplicateWarning,}) => {

    const [isExpanded, setIsExpanded] = useState(false);
    const panelId = `bcr-review-match-panel-${link.id ?? matchIndex}`;
    const matchLabel = t("erps.billermanagement.bcrreview.multiple.matchlabel", "Match {{n}}", {
        n: matchIndex,
    });
    const matchTypeLabel = t(`erps.billermanagement.bcrreview.matchtype.${link.matchType}`, link.matchType);
    const expandLabel = isExpanded
        ? t("erps.billermanagement.bcrreview.multiple.collapse", "Collapse {{match}}", { match: matchLabel })
        : t("erps.billermanagement.bcrreview.multiple.expand", "Expand {{match}}", { match: matchLabel });

    return (
        <VStack spacing={0} className="bcr-review__match-card">
            <HStack
                direction="row"
                alignItems="center"
                spacing={1}
                className="bcr-review__match-header"
            >
                <button
                    type="button"
                    className="bcr-review__expand-toggle"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    aria-label={expandLabel}
                    onClick={() => setIsExpanded((expanded) => !expanded)}
                >
                    <KeyboardArrowDown
                        className={
                            isExpanded
                                ? "bcr-review__chevron bcr-review__chevron--expanded"
                                : "bcr-review__chevron"
                        }
                        aria-hidden="true"
                    />
                </button>
                <FormControlLabel
                    className="bcr-review__match-radio"
                    value={link.id?.toString()}
                    control={<Radio />}
                    label={
                        <Typography variant="body1" component="span" className="bcr-review__similarity">
                            {matchLabel}
                            <strong>{link.targetCentralizedBillerId}</strong>
                            <span className="bcr-review__similarity-value">
                {t("erps.billermanagement.bcrreview.namesimilarity", "Name similarity: {{value}}%", {
                    value: link.nameSimilarity,
                })}
              </span>
                            <strong>{matchTypeLabel}</strong>
                        </Typography>
                    }
                />
            </HStack>
            <div id={panelId} hidden={!isExpanded} className="bcr-review__match-expanded">
                <BcrReviewMatchSection
                    t={t}
                    link={link}
                    bcrDto={link.billerCrossReferenceDto}
                    pendingBcr={pendingBcr}
                    incomingFiKey={incomingFiKey}
                    incomingFiLabel={incomingFiLabel}
                    notAvailableLabel={notAvailableLabel}
                    hideDuplicateWarning={hideDuplicateWarning}
                    hideHeader
                />
            </div>
        </VStack>
    );
};

export default BcrReviewMultipleMatchSection;
